// AGC: useWizardTelemetry — fires view/complete/abandon events for a wizard.
// AGC: Sets flow context on mount, clears on unmount.
import { useEffect, useRef } from 'react'
import { track, setFlowContext, clearFlowContext } from '../lib/analytics'
import type { WizardName } from '../lib/analytics-events'

export function useWizardTelemetry(wizardName: WizardName, entryPoint?: string) {
  const mountedAt = useRef<number>(Date.now())
  const completed = useRef<boolean>(false)
  const lastStep = useRef<{ number: number; name: string; enteredAt: number } | null>(null)
  const stepsViewed = useRef<number>(0)
  // AGC: flowId stored but not directly used — flow context is managed via setFlowContext/clearFlowContext
  const flowId = useRef<string>(setFlowContext(entryPoint ?? wizardName))

  const viewStep = (stepNumber: number, stepName: string): void => {
    lastStep.current = { number: stepNumber, name: stepName, enteredAt: Date.now() }
    stepsViewed.current += 1
    void track('wizard_step_view', {
      wizard_name: wizardName,
      step_number: stepNumber,
      step_name: stepName,
    })
  }

  const complete = (): void => {
    completed.current = true
    void track('wizard_complete', {
      wizard_name: wizardName,
      duration_ms: Date.now() - mountedAt.current,
      step_count: stepsViewed.current,
    })
  }

  useEffect(() => {
    return () => {
      if (!completed.current && lastStep.current) {
        void track('wizard_abandon', {
          wizard_name: wizardName,
          last_step_number: lastStep.current.number,
          last_step_name: lastStep.current.name,
          time_on_step_ms: Date.now() - lastStep.current.enteredAt,
        })
      }
      clearFlowContext()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // AGC: suppress unused variable warning — flowId is used to establish the flow context on mount
  void flowId

  return { viewStep, complete }
}
